// src/components/kuestion/campus/CampusShell.jsx
// The examiner frame: labelled rail on the left, org header on top, page below.
import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useCampus } from './CampusContext';
import AppRail from '../shared/AppRail';
import { TourProvider, useTour } from '../shared/Tour';
import { PreferencesProvider, usePreferences } from '../shared/PreferencesContext';
import AccountMenu from '../shared/AccountMenu';
import AppNoticeboard from '../shared/AppNoticeboard';
import { GridIcon, StudentsIcon, ClipboardIcon, ChartIcon, FileIcon, DatabaseIcon, CardIcon, ShieldIcon } from '../shared/Icons';
import { kq, kqRel } from '../../../config/routes';

const NAV = [
  { key: 'dashboard', tour: 'ex-dash', to: '', label: 'Dashboard', icon: <GridIcon />, match: (p) => p === '' },
  { key: 'students', tour: 'ex-students', to: 'students', label: 'Students', icon: <StudentsIcon />, match: (p) => p.startsWith('students') },
  { key: 'assignments', tour: 'ex-assignments', to: 'assignments', label: 'Assignments', icon: <ClipboardIcon />, match: (p) => p.startsWith('assignments') },
  { key: 'analytics', tour: 'ex-analytics', to: 'analytics', label: 'Analytics', icon: <ChartIcon />, match: (p) => p.startsWith('analytics') },
  { key: 'templates', tour: 'ex-templates', to: 'templates', label: 'Templates', icon: <FileIcon />, match: (p) => p.startsWith('templates') },
  { key: 'banks', to: 'banks', label: 'Data', title: 'Question banks & data', icon: <DatabaseIcon />, match: (p) => p.startsWith('banks') },
  { key: 'billing', tour: 'ex-billing', to: 'billing', label: 'Billing', icon: <CardIcon />, match: (p) => p.startsWith('billing') },
];

const crumbFor = (p, n) => {
  if (p === '') return 'Dashboard';
  if (p === 'students') return `Students · ${n} in this organisation`;
  if (p === 'students/new') return 'Students · add one';
  if (p === 'students/import') return 'Students · CSV import';
  if (p.startsWith('students/')) return 'Students · detail';
  if (p === 'assignments') return 'Assignments';
  if (p === 'assignments/new') return 'Assignments · new';
  if (p === 'analytics') return 'Analytics · cohort';
  if (p.startsWith('analytics/students')) return 'Analytics · student report';
  if (p === 'templates') return 'Exam templates';
  if (p === 'templates/build') return 'Exam templates · build';
  if (p === 'banks') return 'Question banks & data';
  if (p === 'billing') return 'Billing';
  return '';
};

// The rail tells the tour which section was opened, so a first visit gets its intro.
function Rail(props) {
  const tour = useTour();
  return <AppRail {...props} onGo={(n) => { props.onGo(n); if (tour) tour.visit(n.key); }} />;
}

// Every formatted time reads the display store; a zone or clock change bumps
// the preferences version, and keying the page on it re-renders them all.
function Page() {
  const p = usePreferences();
  return <Outlet key={p ? p.version : 0} />;
}

export default function CampusShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { org, students } = useCampus();
  const token = user?.token;

  // Path relative to /kuestion
  const rel = kqRel(location.pathname);
  const active = (NAV.find((n) => n.match(rel)) || NAV[0]).key;
  const name = user?.firstName ? `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}` : (user?.username || 'Examiner');
  const initial = (name[0] || 'E').toUpperCase();

  const handleLogout = async () => {
    await logout();
    navigate(kq('login'));
  };

  return (
    <PreferencesProvider token={token} onInvalidToken={handleLogout}>
    <TourProvider mode="campus" userId={user?.username} firstName={user?.firstName} orgName={org?.display_name}>
    <div className="kqc">
      <div className="kqc-app">
        <Rail
          items={NAV}
          active={active}
          onGo={(n) => navigate(n.to === '' ? kq() : kq(`${n.to}`))}
          onLogout={handleLogout}
          label="Kuestion Campus"
        />

        <div className="kqc-main">
          <header className="kqc-header">
            <div style={{ minWidth: 0 }}>
              <div className="kqc-header__org">
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{org?.display_name}</span>
                <span className="kqc-header__prefix">{org?.prefix}</span>
              </div>
              <div className="kqc-header__crumb">{crumbFor(rel, students.length)}</div>
            </div>
            <span className="kqc-header__spacer" />
            <span className="tag tag-outline"><ShieldIcon />Your organisation only</span>
            <AppNoticeboard />
            <AccountMenu name={name} role="Examiner · owner" initial={initial} onLogout={handleLogout} />
          </header>
          <Page />
        </div>
      </div>
    </div>
    </TourProvider>
    </PreferencesProvider>
  );
}
